import React, { Dispatch, SetStateAction } from 'react';
import { Box } from '@mui/material';
import RichTextEditor from '../../../../../../ui/RichTextEditor';
import {
  JobDescription,
  JobPostingTabData,
} from '../../../../../../utils/types/JobOpenings';
import TranslationPanel from './TranslationPanel';
import { useJobPostDetails } from './Hooks/UseJobPostDetails';
import HeaderCard from './Components/HeaderCard';
import UnifiedActions from './Components/UnifiedActions';
import PreviewModal from './Components/PreviewModal';

type JobPostDetailsProps = {
  jobPostingData: JobPostingTabData;
  setJobPostingData: React.Dispatch<React.SetStateAction<JobPostingTabData>>;
  jobDescription: JobDescription;
  setActiveTab: (value: ((prevState: string) => string) | string) => void;
  orgCompanyDetails: string;
  orgPerksAndBenefits: string;
  /** ID from UseJobPostingTabs — always correct for existing posts */
  jobPostId?: string;
  setJobPostId: Dispatch<SetStateAction<string>>;
  setStages: (value: any) => void;
};

export const JobPostDetails: React.FC<JobPostDetailsProps> = (props) => {
  const {
    resolvedPostId,
    setEditorInstance,
    liveHtml,
    isPreviewOpen,
    setIsPreviewOpen,
    isSaving,
    variables,
    activeJsonTemplate,
    editorKey,
    isImported,
    insertAll,
    undoImports,
    handleLoadTranslation,
    handleTranslationsChanged,
    saveTemplate,
    saveCurrentTemplate,
    getCurrentEditorContent,
    handleSaveOrCreate,
  } = useJobPostDetails(props);

  return (
    <Box sx={{ position: 'relative', display: 'flex', minWidth: 0, flexDirection: 'column', gap: 3, py: 2 }}>
      {/* Header Toolbar */}
      <HeaderCard
        onPreviewClick={() => setIsPreviewOpen(true)}
        onImportClick={isImported ? undoImports : insertAll}
        isImported={isImported}
      />

      {/* Translations Area */}
      {resolvedPostId && (
        <TranslationPanel
          jobPostingId={resolvedPostId}
          availableLanguages={props.jobPostingData.availableLanguages ?? []}
          currentJsonTemplate={props.jobPostingData.jsonTemplate ?? activeJsonTemplate}
          currentHtmlTemplate={props.jobPostingData.htmlTemplate ?? ''}
          onLoadTranslation={handleLoadTranslation}
          onTranslationsChanged={handleTranslationsChanged}
          onBeforeSuggest={saveCurrentTemplate}
          onGetCurrentContent={getCurrentEditorContent}
        />
      )}

      {/* Editor Canvas */}
      <Box sx={{ minWidth: 0 }}>
        <RichTextEditor
          key={editorKey}
          jsonStringTemplate={activeJsonTemplate}
          saveTemplate={saveTemplate}
          validVariables={variables}
          value={props.jobPostingData.language ? undefined : props.jobPostingData.htmlTemplate}
          onEditorReady={setEditorInstance}
          hideSaveButton={true}
        />
      </Box>

      {/* Unified Bottom Page Actions Banner */}
      <UnifiedActions
        resolvedPostId={resolvedPostId}
        isSaving={isSaving}
        jobPostingData={props.jobPostingData}
        onSaveOrCreate={handleSaveOrCreate}
      />

      {/* Large Dialog Modal Preview */}
      <PreviewModal
        isOpen={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        jobPostingData={props.jobPostingData}
        resolvedPostId={resolvedPostId}
        liveHtml={liveHtml}
        isSaving={isSaving}
        onSaveOrCreate={handleSaveOrCreate}
        setJobPostId={props.setJobPostId}
        setStages={props.setStages}
      />
    </Box>
  );
};

export default JobPostDetails;
